import type { Card, HandState, PlayerIndex } from "@joker-holdem/core";
import { CardView } from "./CardView.js";
import { CategoryBadge } from "./CategoryBadge.js";
import { formatBb, isPreflopFoldResult, lastActionForSeat } from "../engine/selectors.js";

interface BoardViewProps {
  hand: HandState;
  /** Seat whose latest action should be called out above the pot (docs/UI_REFERENCE.md "フィードバック反映" #8). */
  oppSeat: PlayerIndex;
  /**
   * Hero's hole cards, rendered inside the table-felt frame below the board
   * (2026-07-17 フィードバック #2 -- the frame used to end at the board, with
   * the hero's cards in a separate row below it).
   */
  heroHole: readonly [Card, Card];
}

interface BannerInfo {
  readonly text: string;
  readonly cls: string;
}

function oppActionInfo(hand: HandState, oppSeat: PlayerIndex): BannerInfo | null {
  const entry = lastActionForSeat(hand, oppSeat);
  if (!entry) return null;
  switch (entry.type) {
    case "fold":
      return { text: "Fold", cls: "opp-action-fold" };
    case "check":
      return { text: "Check", cls: "opp-action-call" };
    case "call":
      return { text: "Call", cls: "opp-action-call" };
    case "allIn":
      return { text: `All-in ${formatBb(entry.amount)}`, cls: "opp-action-raise" };
    default:
      return { text: `${entry.type === "bet" ? "Bet" : "Raise"} ${formatBb(entry.amount)}`, cls: "opp-action-raise" };
  }
}

/**
 * Once the hand is decided, the banner above the pot (which otherwise
 * echoes the opponent's last action) instead announces the outcome --
 * this replaces the old large ShowdownPanel band entirely (フィードバック #2).
 */
function resultBannerInfo(hand: HandState, heroSeat: PlayerIndex): BannerInfo | null {
  const result = hand.result;
  if (!result) return null;
  // Preflop folds skip the result UI entirely and auto-advance instantly
  // (フィードバック #2 例外) -- fall back to the plain action banner instead.
  if (isPreflopFoldResult(hand)) return null;
  if (result.winner === "chop") {
    const award = result.potAwarded[heroSeat];
    return { text: `チョップ${award > 0 ? ` +${formatBb(award)}` : ""}`, cls: "result-banner-chop" };
  }
  if (result.winner === heroSeat) {
    return { text: `You win +${formatBb(result.potAwarded[heroSeat])}`, cls: "result-banner-win" };
  }
  return { text: "You lose", cls: "result-banner-lose" };
}

export function BoardView({ hand, oppSeat, heroHole }: BoardViewProps) {
  const heroSeat: PlayerIndex = oppSeat === 0 ? 1 : 0;
  const street = hand.street;
  const jokerRevealed = street !== "preflop";
  const flopRevealed = jokerRevealed;
  const turnRevealed = street === "turn" || street === "river";
  const riverRevealed = street === "river";
  const pot = hand.players[0].totalCommitted + hand.players[1].totalCommitted;
  const banner = resultBannerInfo(hand, heroSeat) ?? oppActionInfo(hand, oppSeat);

  const showdownDetails = hand.result?.endedBy === "showdown" ? hand.result.showdownDetails : undefined;
  const isShowdown = showdownDetails !== undefined;

  return (
    <div className={`table-felt${isShowdown ? " table-felt-showdown" : ""}`}>
      {/* Always rendered (item 4) -- an absent banner still reserves its
          box via visibility: hidden, so board-row's position never shifts
          depending on whether there's currently anything to announce. */}
      <div
        className={`opp-action-banner ${banner?.cls ?? ""}`}
        style={banner ? undefined : { visibility: "hidden" }}
      >
        {banner?.text ?? " "}
      </div>
      <div className="pot-line">
        <span className="pot-label">Pot</span>
        {formatBb(pot)}
      </div>
      <div className="board-row">
        <div className="joker-slot">
          {showdownDetails && (
            <div className="joker-interp-row joker-interp-row-opp">
              <CardView
                card={showdownDetails[oppSeat].jokerCard}
                size="sm"
                className="joker-interp joker-interp-opp"
              />
              <CategoryBadge details={showdownDetails[oppSeat]} />
            </div>
          )}
          <CardView joker size="sm" faceDown={!jokerRevealed} />
          {showdownDetails && (
            <div className="joker-interp-row joker-interp-row-hero">
              <CardView
                card={showdownDetails[heroSeat].jokerCard}
                size="sm"
                className="joker-interp joker-interp-hero"
              />
              <CategoryBadge details={showdownDetails[heroSeat]} />
            </div>
          )}
        </div>
        <CardView card={flopRevealed ? hand.deal.flopReal[0] : undefined} size="sm" />
        <CardView card={flopRevealed ? hand.deal.flopReal[1] : undefined} size="sm" />
        <CardView card={turnRevealed ? hand.deal.turn : undefined} size="sm" />
        <CardView card={riverRevealed ? hand.deal.river : undefined} size="sm" />
      </div>
      {/* At showdown the size comes from `.table-felt-showdown .hero-in-felt .card`
          (same width as the board cards); the `size` prop only sets the
          non-showdown baseline. */}
      <div className={`hero-in-felt${isShowdown ? " hero-in-felt-compact" : ""}`}>
        <CardView card={heroHole[0]} size="lg" />
        <CardView card={heroHole[1]} size="lg" />
      </div>
    </div>
  );
}
